import React, { useState, useEffect } from 'react';
import { X, Info, Save } from 'lucide-react';
import { noteCategories } from './noteCategories';

const CategoryContextOverlay = ({ isOpen, onClose, initialContexts = {}, onSave }) => {
  const [contexts, setContexts] = useState(initialContexts);
  const [showSavedAlert, setShowSavedAlert] = useState(false);
  const [isSaving, setSaving] = useState(false);

  useEffect(() => {
    // Initialize with empty strings for any missing categories
    const initializedContexts = noteCategories.reduce((acc, category) => {
      acc[category.name] = initialContexts[category.name] || '';
      return acc;
    }, {});
    setContexts(initializedContexts);
  }, [initialContexts]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(contexts);
      setShowSavedAlert(true);
      setTimeout(() => {
        setShowSavedAlert(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error saving context:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 py-6">
        <div className="max-w-3xl mx-auto relative">
          <div className="bg-white rounded-lg shadow-xl">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-notebook-pen"><path d="M13.4 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7.4"/><path d="M2 6h4"/><path d="M2 10h4"/><path d="M2 14h4"/><path d="M2 18h4"/><path d="M21.378 5.626a1 1 0 1 0-3.004-3.004l-5.01 5.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"/></svg>
                  <h2 className="text-2xl font-bold text-gray-900">Category Context</h2>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 transition-colors"
                  disabled={isSaving}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Add context to your categories to help AI provide more relevant suggestions
              </p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Info Alert */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Info className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">How this helps</h3>
                    <p className="mt-2 text-sm text-blue-700">
                      Adding context to your categories helps our AI understand your specific needs,
                      goals, and preferences. This allows us to provide more personalized and
                      relevant suggestions for task planning and organization.
                    </p>
                  </div>
                </div>
              </div>

              {/* Category Inputs */}
              <div className="space-y-6">
                {noteCategories.map((category) => (
                  <div key={category.name} className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600">{category.icon}</span>
                      <label className="font-medium text-gray-900">
                        {category.name}
                      </label>
                    </div>
                    <p className="text-sm text-gray-500">{category.description}</p>
                    <textarea
                      placeholder={category.contextPlaceholder}
                      value={contexts[category.name] || ''}
                      onChange={(e) => setContexts({
                        ...contexts,
                        [category.name]: e.target.value
                      })}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                               text-sm"
                      disabled={isSaving}
                    />
                    <p className="text-xs text-gray-500">{category.contextHelp}</p>
                  </div>
                ))}
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className={`flex items-center px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600
                           text-white rounded-md transition-all ${isSaving ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                    }`}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save Context'}
                </button>
              </div>
            </div>
          </div>

          {/* Success Alert */}
          {showSavedAlert && (
            <div className="fixed bottom-4 right-4 bg-green-50 border border-green-200 rounded-md p-4 shadow-lg">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Success</h3>
                  <p className="mt-1 text-sm text-green-700">
                    Category contexts saved successfully
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryContextOverlay;